// src/pages/Dashboard.jsx
import { useEffect, useState } from "react";
import {
  Box, Grid, Paper, Typography, MenuItem, Select, FormControl, InputLabel,
  Container, Divider, Avatar, Link as MuiLink
} from "@mui/material";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import {
  MonetizationOn, ShoppingCart, Visibility, GroupAdd, TrendingDown, Inventory
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { getDashboardData } from "../api/dashboardApi";

const COLORS = ["#4caf50", "#ffb74d", "#e57373", "#64b5f6", "#f06292"];

const iconMap = {
  money: MonetizationOn,
  cart: ShoppingCart,
  eye: Visibility,
  group: GroupAdd,
  down: TrendingDown,
  inventory: Inventory,
};

const StatCard = ({ stat, filter }) => {
  const Icon = iconMap[stat.icon] || Visibility;
  return (
    <Paper elevation={2} sx={{ p: 3, display: "flex", alignItems: "center", borderRadius: 3, height: '100%' }}>
      <Avatar sx={{ bgcolor: stat.color, width: 56, height: 56 }}>
        <Icon />
      </Avatar>
      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">{stat.label}</Typography>
        <Typography variant="h6" fontWeight="bold">{stat.value}</Typography>
        <Typography
          variant="caption"
          color={stat.percentage.includes("-") ? "error.main" : "success.main"}
        >
          {stat.percentage} so với kỳ trước ({filter})
        </Typography>
      </Box>
    </Paper>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [warehouse, setWarehouse] = useState({ totalStock: 0, stockValue: 0 });
  const [topCustomers, setTopCustomers] = useState([]);
  const [totalSold, setTotalSold] = useState(0);
  const [filter, setFilter] = useState("week");

  useEffect(() => {
    getDashboardData(filter)
      .then((data) => {
        setStats(data.statistics);
        setSalesData(data.salesPerformance);
        setTopProducts(data.topProducts);
        setOrderStatus(data.orderStatus);
        setWarehouse(data.warehouse || { totalStock: 0, stockValue: 0 });
        setTopCustomers(data.topCustomers || []);
        setTotalSold(data.totalSoldThisPeriod || 0);
      })
      .catch(console.error);
  }, [filter]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Dashboard */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h5" fontWeight="bold">📊 Bảng điều khiển quản trị</Typography>
        <FormControl size="small">
          <InputLabel>Lọc theo</InputLabel>
          <Select value={filter} label="Lọc theo" onChange={(e) => setFilter(e.target.value)}>
            <MenuItem value="week">Tuần này</MenuItem>
            <MenuItem value="month">Tháng này</MenuItem>
            <MenuItem value="year">Năm nay</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Cards thống kê */}
      <Grid container spacing={3}>
        {stats.map((stat, idx) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={2}
            key={idx}
            sx={{ height: '100%' }}
          >
            <StatCard stat={stat} filter={filter} />
          </Grid>
        ))}
      </Grid>

      {/* Hiệu suất bán hàng & Top sản phẩm */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8} lg={9} xl={9}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: "100%", display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>📈 Hiệu suất bán hàng</Typography>
            {salesData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Visibility sx={{ fontSize: 48, color: "#ccc" }} />
                <Typography variant="body1" color="text.secondary">
                  Không có dữ liệu bán hàng cho {filter}.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="sales" stroke="#1976d2" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} lg={3} xl={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold">🔥 Sản phẩm bán chạy</Typography>
            <Divider sx={{ my: 1 }} />
            {topProducts.length ? topProducts.map((p, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar src={p.image} alt={p.name} sx={{ width: 36, height: 36, mr: 1 }} />
                  <MuiLink component={Link} to={`/products/${p.id}`} underline="hover" fontWeight="500">
                    {p.name}
                  </MuiLink>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography fontWeight="bold">{p.sales} đã bán</Typography>
                  <Typography variant="caption" color="text.secondary">
                    💰 {p.price?.toLocaleString()}₫
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Typography color="text.secondary">Không có dữ liệu sản phẩm.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Trạng thái đơn hàng & Tóm tắt kho */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6} lg={6} xl={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">📦 Trạng thái đơn hàng</Typography>
            <Divider sx={{ my: 1 }} />
            {orderStatus.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={orderStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {orderStatus.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">Không có dữ liệu đơn hàng.</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={6} xl={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">🏬 Tóm tắt kho hàng</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Inventory sx={{ mr: 1, color: "#1976d2" }} />
              Tổng số lượng: <strong style={{ marginLeft: 4 }}>{warehouse.totalStock}</strong>
            </Typography>
            <Typography sx={{ mt: 1 }}>
              💰 Giá trị tồn kho: <strong>{warehouse.stockValue.toLocaleString()}₫</strong>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tổng sản phẩm & Khách hàng */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={4} lg={3} xl={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: "center", height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">🧾 Tổng sản phẩm đã bán</Typography>
            <Typography sx={{ fontSize: 40, fontWeight: "bold", color: "#1976d2", mt: 2 }}>{totalSold}</Typography>
            <Typography color="text.secondary">Trong kỳ: {filter}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9} xl={9}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">👑 Khách hàng tiêu biểu</Typography>
            <Divider sx={{ my: 1 }} />
            {topCustomers.length ? topCustomers.map((c, i) => (
              <Box key={i} sx={{ mt: i === 0 ? 0 : 2, pb: 1, borderBottom: i !== topCustomers.length - 1 ? "1px solid #eee" : "none" }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography fontWeight="bold">{c.name}</Typography>
                    <Typography fontSize={13} color="text.secondary">
                      {c.totalOrders} đơn hàng • {c.totalSpent.toLocaleString()}₫
                    </Typography>
                  </Box>
                  <Typography fontSize={13} color="primary.main">
                    {c.lastOrderDays} ngày trước
                  </Typography>
                </Box>
              </Box>
            )) : (
              <Typography color="text.secondary">Chưa có dữ liệu khách hàng.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
